import { Menu } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { useValidatorsList } from '@/common/hooks/useValidatorsList'
import KeplrConnectButton from '@/common/wallets/keplr/ConnectButton'
import { config } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { convertAddressPrefix } from '@/helpers/cosmos'
import { RoutePaths } from '@/routes'
import { UiButton } from '@/ui/UiButton'
import UiContainer from '@/ui/UiContainer'
import { UiSheet, UiSheetContent, UiSheetTrigger } from '@/ui/UiSheet'

import Logo from '../Logo'

export default function Navbar() {
  const location = useLocation()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const { isMultisigMember, address } = useKeplrWallet()
  const { validators, isLoading: isLoadingValidators } = useValidatorsList()

  const isValidator = (() => {
    if (!address || isLoadingValidators || !validators.length) return false
    const userValoper = convertAddressPrefix(address)
    return validators.some(v => v.operator_address === userValoper)
  })()

  useEffect(() => {
    setIsSheetOpen(false)
  }, [location.pathname])

  return (
    <UiContainer className='flex items-center justify-between gap-4 px-2 py-4 lg:px-0'>
      <div className='flex items-center'>
        <Logo className='mr-6' />
      </div>

      {/* Desktop nav */}
      <nav className='hidden items-center gap-6 md:flex'>
        {isMultisigMember && (
          <Link
            to={RoutePaths.Multisig}
            className={
              'hover:text-primary hover:border-primary/40 block border-b-2 border-transparent px-4 py-3 text-base transition-colors duration-150 ease-out ' +
              (location.pathname.startsWith('/multisig')
                ? 'text-primary border-primary font-semibold'
                : 'text-foreground')
            }
            aria-current={location.pathname.startsWith('/multisig')}
          >
            Multisig
          </Link>
        )}

        {isValidator && (
          <Link
            to={RoutePaths.Governance}
            className={
              'hover:text-primary hover:border-primary/40 block border-b-2 border-transparent px-4 py-3 text-base transition-colors duration-150 ease-out ' +
              (location.pathname.startsWith('/governance')
                ? 'text-primary border-primary font-semibold'
                : 'text-foreground')
            }
            aria-current={location.pathname.startsWith('/governance')}
          >
            Governance
          </Link>
        )}

        <a
          href={config.BRIDGE_APP_LINK}
          className='hover:text-primary text-foreground block border-b-2 border-transparent px-4 py-3 text-base transition-colors duration-150 ease-out'
        >
          Bridge
        </a>

        <KeplrConnectButton />
      </nav>

      {/* Mobile: Hamburger menu */}
      <div className='md:hidden'>
        <UiSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <UiSheetTrigger asChild>
            <UiButton variant='outline' size='icon' aria-label='Open menu'>
              <Menu className='h-5 w-5' />
            </UiButton>
          </UiSheetTrigger>
          <UiSheetContent
            side='right'
            className='text-foreground w-full p-0 sm:max-w-sm'
          >
            <div className='flex flex-col gap-6 p-6'>
              <Logo />

              <nav className='flex flex-col gap-4'>
                {isMultisigMember && (
                  <Link
                    to={RoutePaths.Multisig}
                    className={
                      'hover:text-primary hover:border-primary/40 block border-b-2 border-transparent px-4 py-3 text-base transition-colors duration-150 ease-out ' +
                      (location.pathname.startsWith('/multisig')
                        ? 'text-primary border-primary font-semibold'
                        : 'text-foreground')
                    }
                    aria-current={location.pathname.startsWith('/multisig')}
                  >
                    Multisig
                  </Link>
                )}

                {isValidator && (
                  <Link
                    to={RoutePaths.Governance}
                    className={
                      'hover:text-primary hover:border-primary/40 block border-b-2 border-transparent px-4 py-3 text-base transition-colors duration-150 ease-out ' +
                      (location.pathname.startsWith('/governance')
                        ? 'text-primary border-primary font-semibold'
                        : 'text-foreground')
                    }
                    aria-current={location.pathname.startsWith('/governance')}
                  >
                    Governance
                  </Link>
                )}

                <a
                  href={config.BRIDGE_APP_LINK}
                  className='hover:text-primary text-foreground block border-b-2 border-transparent px-4 py-3 text-base transition-colors duration-150 ease-out'
                >
                  Bridge
                </a>

                <KeplrConnectButton />
              </nav>
            </div>
          </UiSheetContent>
        </UiSheet>
      </div>
    </UiContainer>
  )
}
